import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, Check, Search, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { services } from '@/services/serviceData';
import ServiceCategories, { categories, getCategoryLabel } from '@/components/services/ServiceCategories';
import ServiceCard, { serviceStack } from '@/components/services/ServiceCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

import { CALENDLY_URL } from '@/lib/site';

const faqs = [
  {
    q: 'Do you offer web design services for businesses in Qatar, the Middle East and the USA?',
    a: 'Yes. We work from Doha with businesses across Qatar and the wider Middle East, as well as the USA and Europe. Everything is delivered remotely, so your location is not a constraint.',
  },
  {
    q: 'What is digital branding and how can it help my business?',
    a: 'Digital branding helps your business stand out online, build trust, and attract more customers through a cohesive visual and messaging strategy.',
  },
  {
    q: 'Can you help my business with local SEO?',
    a: 'Absolutely. We offer small business digital solutions including local SEO to help you rank higher in your city or region.',
  },
];

const promises = ['Custom-coded, no page builders', 'You own the code and accounts', 'Serving US businesses, with a team in Doha'];

// Lead with software and AI; the category list order is the display order.
const categoryOrder = categories.map(c => c.value);
const orderedServices = [...services].sort(
  (a, b) => categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)
);

const Services = () => {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setActiveCategory(searchParams.get('category') || 'all');
  }, [searchParams]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: services.length };
    services.forEach(s => { c[s.category] = (c[s.category] || 0) + 1; });
    return c;
  }, []);

  const filteredServices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return orderedServices.filter(service =>
      (activeCategory === 'all' || service.category === activeCategory) &&
      (q === '' ||
        service.title.toLowerCase().includes(q) ||
        service.shortDescription.toLowerCase().includes(q) ||
        service.offerings.some(o => o.name.toLowerCase().includes(q)) ||
        serviceStack(service).some(s => s.toLowerCase().includes(q)))
    );
  }, [activeCategory, searchQuery]);

  const resetFilters = () => {
    setSearchQuery('');
    setActiveCategory('all');
  };

  return (
    <>
      <Helmet>
        <title>Web Design, Software & AI Services for US Businesses | Omnicraft</title>
        <meta name="description" content="Custom-coded websites, software, AI automation and Google Ads management for US small businesses. No page builders, and you own the code." />
      </Helmet>

      <div className="relative isolate min-h-screen overflow-hidden pt-16">
        {/* Ambient glow */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px]">
          <div className="absolute left-1/2 top-[-180px] h-[520px] w-[820px] max-w-full -translate-x-1/2 rounded-full bg-primary/25 blur-[120px]" />
          <div className="absolute right-[-120px] top-40 h-72 w-72 rounded-full bg-violet-500/20 blur-[100px]" />
        </div>

        {/* Hero */}
        <section className="container-custom pb-10 pt-14 md:pt-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> What we build
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight [text-wrap:balance] md:text-6xl">
              Software, AI and design,{' '}
              <span className="bg-gradient-to-r from-primary via-sky-400 to-violet-400 bg-clip-text text-transparent">
                built to actually ship.
              </span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              From rescuing an AI-built app to shipping a full SaaS product, pick what you need or tell us the problem and we will point you to the right service.
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-400">
              {promises.map(p => (
                <li key={p} className="inline-flex items-center gap-2">
                  <Check size={16} className="text-primary" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Filters */}
        <section className="container-custom">
          <div className="flex flex-col gap-4 rounded-3xl border border-slate-200/80 bg-white/70 p-3 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.03] lg:flex-row lg:items-center">
            <label className="relative block lg:w-80">
              <span className="sr-only">Search services</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search: Supabase, MCP, SEO..."
                className="h-11 w-full rounded-full border border-slate-200 bg-white pl-11 pr-10 text-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15 dark:border-white/10 dark:bg-slate-950/60 [&::-webkit-search-cancel-button]:hidden"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </label>
            <div className="min-w-0 flex-1">
              <ServiceCategories activeCategory={activeCategory} setActiveCategory={setActiveCategory} counts={counts} />
            </div>
          </div>

          <p className="mt-5 px-1 text-sm text-slate-500 dark:text-slate-400" aria-live="polite">
            {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'}
            {activeCategory !== 'all' && <> in <span className="font-medium text-foreground">{getCategoryLabel(activeCategory)}</span></>}
            {searchQuery && <> matching <span className="font-medium text-foreground">"{searchQuery}"</span></>}
          </p>
        </section>

        {/* Grid */}
        <section className="container-custom pb-20 pt-6">
          {filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredServices.map(service => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 px-6 py-16 text-center dark:border-white/15">
              <Search className="mx-auto h-8 w-8 text-slate-400" />
              <h2 className="mt-4 text-xl font-semibold">No services match that</h2>
              <p className="mx-auto mt-2 max-w-md text-slate-500 dark:text-slate-400">
                Try a different word, or tell us what you need and we will tell you honestly whether we can help.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button variant="outline" className="rounded-full" onClick={resetFilters}>Clear filters</Button>
                <Button asChild className="rounded-full"><Link to="/contact">Ask us</Link></Button>
              </div>
            </div>
          )}
        </section>

        {/* FAQ */}
        <section className="border-t border-slate-200/80 bg-slate-50/60 py-16 dark:border-white/10 dark:bg-white/[0.02]">
          <div className="container-custom grid gap-10 lg:grid-cols-[1fr_2fr]">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Questions, answered</h2>
              <p className="mt-3 text-slate-600 dark:text-slate-400">Anything else, just ask on WhatsApp or book a call.</p>
            </div>
            <Accordion type="single" collapsible className="rounded-3xl border border-slate-200/80 bg-white/80 px-6 dark:border-white/10 dark:bg-white/[0.03]">
              {faqs.map(({ q, a }, i) => (
                <AccordionItem key={q} value={`faq-${i}`} className={i === faqs.length - 1 ? 'border-b-0' : ''}>
                  <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 dark:text-slate-400">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(({ q, a }) => ({
              "@type": "Question",
              "name": q,
              "acceptedAnswer": { "@type": "Answer", "text": a },
            })),
          }) }} />
        </section>

        {/* CTA */}
        <section className="container-custom py-16">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary via-blue-600 to-violet-600 px-6 py-12 text-white shadow-2xl shadow-primary/20 md:px-12">
            <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="max-w-xl">
                <h2 className="text-3xl font-bold tracking-tight">Not sure which one you need?</h2>
                <p className="mt-2 text-white/85">Describe the problem in a 30-minute call. We will tell you what we would build, and whether it is worth building.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" variant="secondary" className="rounded-full">
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Calendar className="mr-2 h-4 w-4" /> Book a call
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white hover:text-primary">
                  <Link to="/contact">Contact us <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
